import { Button, Link, TextArea, TextInput, Typography } from "@fullscript/aviary-web";
import type { SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmailInput } from "@shared/components/EmailInput/EmailInput";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { areRequiredFieldsValid, isNameInvalid } from "./helpers";

import * as styles from "./PatientConnectSignUpForm.styles";

interface Props {
  signInHref: string;
}

const PatientConnectSignUpForm = ({ signInHref }: Props) => {
  const { t } = useTranslation();
  const { emailRegex } = useGlobalConfig();

  const [firstName, setFirstName] = useState("");
  const [isFirstNameDirty, setIsFirstNameDirty] = useState(false);
  const [lastName, setLastName] = useState("");
  const [isLastNameDirty, setIsLastNameDirty] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [additionalInformation, setAdditionalInformation] = useState("");

  const canSubmit = areRequiredFieldsValid({ firstName, lastName, email }, emailRegex);

  // The DS5 field renders its own "Email is required" caption, so the shared wrapper is
  // only asked to validate once there is a value — otherwise both emit that same message.
  const shouldValidateEmail = isEmailDirty && !!email;

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
  };

  return (
    <div css={styles.card}>
      <form onSubmit={handleSubmit} css={styles.form} noValidate data-e2e="connect-sign-up-form">
        <TextInput
          id="firstName"
          name="firstName"
          label={t(l.common.FirstName)}
          value={firstName}
          onChange={event => setFirstName(event.target.value)}
          onBlur={() => setIsFirstNameDirty(true)}
          errorCaption={
            isNameInvalid(firstName, isFirstNameDirty) ? t(l.common.FirstNameInvalid) : undefined
          }
          required
        />
        <TextInput
          id="lastName"
          name="lastName"
          label={t(l.common.LastName)}
          value={lastName}
          onChange={event => setLastName(event.target.value)}
          onBlur={() => setIsLastNameDirty(true)}
          errorCaption={
            isNameInvalid(lastName, isLastNameDirty) ? t(l.common.LastNameInvalid) : undefined
          }
          required
        />
        <EmailInput
          designSystemVersion="ds5"
          value={email}
          setEmail={setEmail}
          onChange={event => setEmail(event.target.value)}
          onBlur={() => setIsEmailDirty(true)}
          isDirty={shouldValidateEmail}
          hideSuggestion
          required
        />
        <TextArea
          id="additionalInformation"
          name="additionalInformation"
          aria-label={t(l.connect.form.additionalInformation)}
          placeholder={t(l.connect.form.additionalInformation)}
          rows={2}
          noResize
          value={additionalInformation}
          onChange={event => setAdditionalInformation(event.target.value)}
        />
        <div css={styles.submitRow}>
          <Button type="submit" size="large" isFullWidth disabled={!canSubmit}>
            {t(l.common.CreateAccount)}
          </Button>
        </div>
        <div css={styles.signInPrompt}>
          <Typography variant="footnote" isMarginless>
            {t(l.connect.form.alreadyHaveAnAccount)}
          </Typography>
          <Link href={signInHref} hideUnderline>
            <Typography as="span" variant="footnote" fontWeight="bold" isMarginless>
              {t(l.common.SignIn)}
            </Typography>
          </Link>
        </div>
      </form>
    </div>
  );
};

export { PatientConnectSignUpForm };
